import { z } from 'zod';
import { cancellationFeeSchema } from '../appointment-types/types';

// =============================================================================
// Time Slot Types
// =============================================================================

/**
 * Available time slot for booking.
 *
 * Three provider-name flavors travel together:
 *   - `providerName`        raw `firstName + lastName` from the DB (may
 *                           carry mangled EHR data — kept for backward
 *                           compat with existing voice consumers).
 *   - `providerDisplayName` cleaned form for UI lists ("Anurag Sahai, MD").
 *   - `providerSpokenName`  TTS-safe form for voice agents
 *                           ("Doctor Anurag Sahai").
 *   - `providerCredential`  extracted credential (MD, NP, …) or null.
 *
 * Both display + spoken fields are computed by `formatProviderName` so
 * every consumer sees the same canonical shape regardless of how the
 * EHR mangled the underlying record.
 */
export const timeSlotSchema = z.object({
  startTime: z.string(), // ISO date string
  endTime: z.string(), // ISO date string
  durationMinutes: z.number(),
  providerId: z.string().uuid(),
  providerName: z.string(),
  providerDisplayName: z.string(),
  providerSpokenName: z.string(),
  providerCredential: z.string().nullable(),
  locationId: z.string().uuid(),
  locationName: z.string(),
  locationTimezone: z.string(), // IANA timezone (e.g., "America/Los_Angeles")
  displayTime: z.string().optional(), // Human-readable format for speech
});

export type TimeSlot = z.infer<typeof timeSlotSchema>;

/**
 * Request to get available slots.
 */
export const getAvailableSlotsRequestSchema = z.object({
  providerId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  appointmentTypeId: z.string().uuid().optional(),
  daysAhead: z.number().int().min(1).max(90).optional().default(14),
  // 500, matching `offerableSlotsRequestSchema.maxSlots`: agent-core asks for a
  // whole day at once (WIDE_SEARCH_COUNT) and a lower cap here rejected the
  // request outright, which the voice agent then read as an empty day.
  count: z.number().int().min(1).max(500).optional().default(5),
  // One block or several — a slot matches if it falls in ANY of them. Mirrors
  // the offerable-slots engine's contract (ADR-082); single values stay valid.
  preferredTimeOfDay: z
    .union([
      z.enum(['morning', 'afternoon', 'evening']),
      z.array(z.enum(['morning', 'afternoon', 'evening'])).min(1),
    ])
    .optional(),
  preferredDaysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
  /**
   * `HH:MM` lower bound on slot start time (24h, server-local). Used by
   * the voice agent's relative-anchor parser — "later in the morning"
   * with an offered slot at 10:00 sends `earliestTime: "10:00"` so the
   * API filters out slots before that moment.
   */
  earliestTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  /** `HH:MM` upper bound, mirror of `earliestTime`. */
  latestTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  /**
   * Inclusive search-window start, ISO date YYYY-MM-DD. When set with
   * `toDate`, overrides `daysAhead`. Used by the voice agent to narrow
   * a scan to a specific day/week the patient requested.
   */
  fromDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** Inclusive search-window end, ISO date YYYY-MM-DD. */
  toDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /**
   * Which end of the window `count` is taken from; the slots still come back
   * earliest-first. `latest` is "the latest you have": without it a window
   * holding more than `count` openings answers with the latest of its EARLIEST
   * ones. Mirrors `offerableSlotsRequestSchema.sortBy`.
   */
  sortBy: z.enum(['earliest', 'latest']).optional(),
});

/** What a caller sends: `daysAhead` and `count` may be omitted (14 and 5). */
export type GetAvailableSlotsRequest = z.input<typeof getAvailableSlotsRequestSchema>;

/**
 * Response with available slots.
 */
export const getAvailableSlotsResponseSchema = z.object({
  slots: z.array(timeSlotSchema),
});

export type GetAvailableSlotsResponse = z.infer<typeof getAvailableSlotsResponseSchema>;

// =============================================================================
// Warm Availability
// =============================================================================

/**
 * Pre-warm the availability cache for a scope before/at a call. The one
 * direction-specific lever: outbound calls it pre-call with full scope;
 * inbound calls it coarse at answer (no provider) and again JIT once the
 * provider is known. The API routes per-connector — Athena does a live
 * scoped pull and upsert; Office Ally relies on its background push.
 */
export const warmAvailabilityRequestSchema = z.object({
  locationId: z.string().uuid(),
  providerId: z.string().uuid().optional(),
  appointmentTypeId: z.string().uuid().optional(),
  horizonDays: z.number().int().min(1).max(90).optional().default(14),
  /** Outbound Athena pre-call may block on the live pull; inbound coarse warm = false. */
  blocking: z.boolean().optional().default(false),
});

export type WarmAvailabilityRequest = z.infer<typeof warmAvailabilityRequestSchema>;

/**
 * - `live`       — a connector live pull ran and upserted slots (Athena).
 * - `background` — no live pull; served from the connector's background push (OA).
 * - `skipped`    — nothing to warm (e.g. location not EHR-mapped).
 */
export const warmAvailabilitySourceSchema = z.enum(['live', 'background', 'skipped']);
export type WarmAvailabilitySource = z.infer<typeof warmAvailabilitySourceSchema>;

export const warmAvailabilityResponseSchema = z.object({
  warmed: z.boolean(),
  source: warmAvailabilitySourceSchema,
  slotsUpserted: z.number().int().nonnegative(),
  freshAsOf: z.string().nullable(),
});

export type WarmAvailabilityResponse = z.infer<typeof warmAvailabilityResponseSchema>;

/**
 * Bulk create slots request (admin/seed endpoint).
 */
export const bulkCreateSlotsRequestSchema = z.object({
  slots: z.array(
    z.object({
      providerId: z.string().uuid(),
      locationId: z.string().uuid(),
      startTime: z.string().datetime(),
      endTime: z.string().datetime(),
      durationMinutes: z.number().int().min(5).max(480),
      appointmentTypeId: z.string().uuid().optional(),
    })
  ),
});

export type BulkCreateSlotsRequest = z.infer<typeof bulkCreateSlotsRequestSchema>;

/**
 * Bulk create slots response.
 */
export const bulkCreateSlotsResponseSchema = z.object({
  created: z.number(),
});

export type BulkCreateSlotsResponse = z.infer<typeof bulkCreateSlotsResponseSchema>;

// =============================================================================
// Appointment Types
// =============================================================================

/**
 * Appointment status enum.
 */
export const appointmentStatusSchema = z.enum([
  'proposed',
  'pending',
  'booked',
  'arrived',
  'fulfilled',
  'cancelled',
  'noshow',
  'entered-in-error',
]);

export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

/**
 * Booking channel enum.
 */
export const bookingChannelSchema = z.enum([
  'phone',
  'sms',
  'web',
  'chat',
  'walk-in',
  'ehr',
  'api',
  'staff',
]);

export type BookingChannel = z.infer<typeof bookingChannelSchema>;

/** Reply to `POST /api/appointments/:id/ehr-resync` (ADR-284): the row is queued, not yet synced. */
export const ehrResyncResponseSchema = z.object({
  appointment: z.object({ id: z.string().uuid() }),
});
export type EhrResyncResponse = z.infer<typeof ehrResyncResponseSchema>;

/**
 * `GET /api/appointments/test-booking` (ADR-293): whether the caller may add a
 * test appointment here. `access: null` means no button.
 */
export const testBookingAccessResponseSchema = z.object({
  access: z.object({ via: z.literal('platform_admin') }).nullable(),
});
export type TestBookingAccessResponse = z.infer<typeof testBookingAccessResponseSchema>;

/**
 * `POST /api/appointments/test-booking` (ADR-293). `date` and `time` are the
 * clinic's wall clock, read in the timezone of the location the server picks.
 */
export const testBookingRequestSchema = z.object({
  patientId: z.string().uuid(),
  appointmentTypeId: z.string().uuid(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be HH:mm'),
});
export type TestBookingRequest = z.infer<typeof testBookingRequestSchema>;

export const testBookingResponseSchema = z.object({
  appointment: z.object({
    id: z.string().uuid(),
    startTime: z.string(),
    endTime: z.string(),
    providerId: z.string().uuid(),
    locationId: z.string().uuid(),
    confirmationNumber: z.string(),
  }),
});
export type TestBookingResponse = z.infer<typeof testBookingResponseSchema>;

/**
 * Why a patient cancelled, as a category (ADR-283).
 *
 * `not_given` means they were asked and gave no reason; a null code means
 * nobody asked. Mirrors `CANCEL_REASON_CODES` in `@vagusx/agent-core` — a
 * livekit type test keeps the two identical.
 */
export const CANCELLATION_REASON_CODES = [
  'schedule_conflict',
  'feeling_better',
  'transportation',
  'cost_or_insurance',
  'other_provider',
  'other',
  'not_given',
] as const;

export const cancellationReasonCodeSchema = z.enum(CANCELLATION_REASON_CODES);
export type CancellationReasonCode = z.infer<typeof cancellationReasonCodeSchema>;

/** How each category reads to the front office. */
export const CANCELLATION_REASON_LABELS: Record<CancellationReasonCode, string> = {
  schedule_conflict: 'Schedule conflict',
  feeling_better: 'Feeling better',
  transportation: 'Transportation',
  cost_or_insurance: 'Cost or insurance',
  other_provider: 'Seeing another provider',
  other: 'Other',
  not_given: 'No reason given',
};

/**
 * Scheduled appointment response.
 */
export const appointmentResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  patientId: z.string().uuid().nullable(),
  providerId: z.string().uuid().nullable(),
  locationId: z.string().uuid().nullable(),
  startTime: z.string(),
  endTime: z.string(),
  status: z.number(), // Stored as smallint
  appointmentTypeId: z.string().uuid().nullable(),
  reasonForVisit: z.string().nullable(),
  notes: z.string().nullable(),
  isTelehealth: z.boolean(),
  telehealthUrl: z.string().nullable(),
  confirmationNumber: z.string().nullable(),
  bookedVia: z.number().nullable(),
  /** The EHR's id for this appointment; null until the booking has reached the EHR. */
  ehrAppointmentId: z.string().nullable().optional(),
  // EHR write-back state (ADR-092). smallint: 0 pending / 1 synced / 2 failed /
  // 3 not-applicable. Optional — the detail (getById) and by-patient responses
  // carry it; the schedule view doesn't.
  ehrSyncStatus: z.number().optional(),
  ehrSyncError: z.string().nullable().optional(),
  ehrSyncErrorCode: z.string().nullable().optional(), // Registered EHR error code (ADR-253)
  /** Last time the EHR and this row agreed: a write that landed, or a pull from the EHR (ADR-284). */
  lastEhrSyncAt: z.string().nullable().optional(),
  // Why it was cancelled (ADR-283). The words are the patient's and can carry
  // health details: display them, never log them.
  cancellationReason: z.string().nullable().optional(),
  // Stored as TEXT (ADR-345). A code since dropped from the list reads as null
  // rather than failing the whole appointment.
  cancellationReasonCode: cancellationReasonCodeSchema.nullable().optional().catch(null),
  cancelledAt: z.string().nullable().optional(),
  // The late-cancellation fee the patient accepted when this was cancelled
  // (ADR-290), in cents. Null when none applied — staff bill from this.
  cancellationFeeCents: z.number().int().nullable().optional(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export type AppointmentResponse = z.infer<typeof appointmentResponseSchema>;

/**
 * Appointment with relations (provider, location, patient names).
 */
export const appointmentWithRelationsSchema = appointmentResponseSchema.extend({
  provider: z
    .object({
      id: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
      specialty: z.string().nullable().optional(),
      // How the voice agent says this provider's name (ADR-321). Set on the
      // patient appointment lists (`GET /api/appointments?patientId=`), which
      // the voice agent reads for cancel/reschedule; optional because the
      // single-appointment read does not carry it.
      spokenName: z.string().optional(),
    })
    .nullable(),
  location: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      address: z.string().nullable().optional(),
      timezone: z.string(),
    })
    .nullable(),
  patient: z
    .object({
      id: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
      phone: z.string().nullable().optional(),
    })
    .nullable(),
  // The appointment type's late-cancellation fee (ADR-290). Only the upcoming
  // list carries it — it is what the voice agent discloses before a cancel.
  cancellationFee: cancellationFeeSchema.nullable().optional(),
});

export type AppointmentWithRelations = z.infer<typeof appointmentWithRelationsSchema>;

/**
 * Book appointment request.
 */
export const bookAppointmentRequestSchema = z.object({
  patientId: z.string().uuid(),
  providerId: z.string().uuid(),
  locationId: z.string().uuid(),
  startTime: z.string().datetime(),
  durationMinutes: z.number().int().min(5).max(480).optional().default(30),
  appointmentTypeCode: z.string().optional(),
  // Alternative to appointmentTypeCode — the type's UUID. The API resolves the
  // code from it so booking constraint validation runs (the voice agent holds
  // the id, not the code).
  appointmentTypeId: z.string().uuid().optional(),
  reasonForVisit: z.string().optional(),
  // A caller that does not say how it booked is an API integration; every
  // first-party caller (web, voice, SMS) names its channel.
  bookedVia: bookingChannelSchema.optional().default('api'),
  callRoomName: z.string().optional(),
  // Booking attribution
  bookingAgentId: z.string().optional(),
  bookingSource: z.string().optional(),
  bookingSessionId: z.string().optional(),
  // Referral task this booking is for. When present, the API links the booked
  // appointment back onto the task so the Referrals worklist shows it.
  taskId: z.string().uuid().optional(),
  // The interaction (voice call / chat) this booking was made ON. Persisted on
  // the appointment row so the post-call evaluator can prove a booking was
  // committed during THIS call — `interactions.appointment_id` cannot, since it
  // is also set at call creation for reminder calls about an existing
  // appointment. Optional: front-office manual bookings have no interaction.
  interactionId: z.string().uuid().optional(),
  // Book despite overridable booking-constraint violations; the API then
  // requires `overrideReason`. Non-overridable violations still refuse.
  override: z.boolean().optional().default(false),
  overrideReason: z.string().optional(),
});

/** What a caller sends: fields with defaults may be omitted. */
export type BookAppointmentRequest = z.input<typeof bookAppointmentRequestSchema>;

/**
 * Book appointment response.
 */
export const bookAppointmentResponseSchema = z.object({
  success: z.boolean(),
  appointment: appointmentResponseSchema.optional(),
  confirmationNumber: z.string().optional(),
  error: z.string().optional(),
  /** Registered EHR error code when the EHR refused the booking (ADR-252). */
  errorCode: z.string().optional(),
  /**
   * Booked on our side but held: the EHR could not take it yet. It lands when the
   * EHR recovers, and a staff task follows up if it never does (ADR-254).
   */
  ehrConfirmationPending: z.boolean().optional(),
});

export type BookAppointmentResponse = z.infer<typeof bookAppointmentResponseSchema>;

/**
 * Cancel appointment request.
 */
/** Longest cancel `reason` the API takes (ADR-283); the web field is capped to match. */
export const CANCEL_REASON_MAX_CHARS = 500;

export const cancelAppointmentRequestSchema = z.object({
  /** Free text — the patient's own words, or staff's note. */
  reason: z.string().max(CANCEL_REASON_MAX_CHARS).optional(),
  /** The category (ADR-283). */
  reasonCode: cancellationReasonCodeSchema.optional(),
  // The interaction this cancellation was committed on, so the post-call
  // evaluator can see it. Its own column, not `interaction_id`: that one means
  // "booked on this call", and G-CONFAB reads it that way.
  interactionId: z.string().uuid().optional(),
  // The late-cancellation fee the patient was told about and accepted before
  // this cancel (ADR-290), in cents. Stored as disclosed, so staff bill what
  // the patient actually agreed to.
  cancellationFeeCents: z.number().int().positive().optional(),
});

export type CancelAppointmentRequest = z.infer<typeof cancelAppointmentRequestSchema>;

/**
 * Reschedule appointment request.
 */
export const rescheduleAppointmentRequestSchema = z.object({
  newStartTime: z.string().datetime(),
  // The interaction this move was committed on. A reschedule is a commitment
  // the patient hears on THIS call, so the post-call evaluator's G-CONFAB gate
  // has to be able to see it — otherwise an agent that correctly moved an
  // appointment and said so is scored as having confabulated a booking.
  // Only stamped when the appointment carries no interaction yet; see
  // `ScheduledAppointmentService.rescheduleAppointment`.
  interactionId: z.string().uuid().optional(),
});

export type RescheduleAppointmentRequest = z.infer<typeof rescheduleAppointmentRequestSchema>;

/**
 * Why a booking or reschedule was refused before the EHR was asked (ADR-313). A
 * reschedule's error `code` is the refusal; a booking keeps `BOOKING_FAILED` and
 * carries it as `details.refusal`. Either way `details.description` is the
 * voice-agent line, so a caller can say the specific thing, not "taken".
 *   - SLOT_TYPE_MISMATCH: the time is open, but only for other appointment types.
 */
export const SLOT_REFUSAL = { SLOT_TYPE_MISMATCH: 'SLOT_TYPE_MISMATCH' } as const;

export type SlotRefusal = (typeof SLOT_REFUSAL)[keyof typeof SLOT_REFUSAL];

// =============================================================================
// Call Intake Snapshot (voice-agent call-collected details)
// =============================================================================

/**
 * Where a confirmed detail's value came from on the call. Mirrors the
 * voice form engine's `Provenance` (apps/livekit .../form/engine.ts) so
 * the front office can see WHAT changed, not just the final value:
 *   - `on-file`           the on-file value was confirmed unchanged.
 *   - `patient-corrected` the patient corrected an on-file value.
 *   - `collected`         no value was on file; the patient supplied one.
 *   - `absent`            nothing on file and nothing collected.
 */
export const callDetailProvenanceSchema = z.enum([
  'on-file',
  'patient-corrected',
  'collected',
  'absent',
]);

export type CallDetailProvenance = z.infer<typeof callDetailProvenanceSchema>;

/** A single confirmed/corrected detail captured during the call. */
export const callDetailSchema = z.object({
  value: z.string().nullable(),
  provenance: callDetailProvenanceSchema,
});

export type CallDetail = z.infer<typeof callDetailSchema>;

/** One note/request the patient raised for the team during the call. */
export const callFrontOfficeNoteSchema = z.object({
  details: z.string(),
  urgency: z.enum(['low', 'normal', 'high']),
  topic: z.string(),
});

export type CallFrontOfficeNote = z.infer<typeof callFrontOfficeNoteSchema>;

/**
 * The bundle of details a voice call collects for a booked appointment.
 * Persisted onto `scheduled_appointments.call_intake_snapshot` via a
 * call-end finalize patch (data accrues across the whole call — before
 * AND after the booking event — so it can't be captured atomically at
 * booking time). `demographics` is null when the confirm-details step
 * never ran (e.g. a non-referral flow with no chart-confirmation step).
 *
 * NOTE: PHI/PII (address, insurance, phone, free-text notes) — never log
 * the values; audit entries record counts + provenance only.
 */
export const callIntakeSnapshotSchema = z.object({
  demographics: z
    .object({
      phone: callDetailSchema.nullable(),
      address: callDetailSchema.nullable(),
      insurance: callDetailSchema.nullable(),
    })
    .nullable(),
  frontOfficeNotes: z.array(callFrontOfficeNoteSchema),
  capturedAt: z.string(), // ISO timestamp
});

export type CallIntakeSnapshot = z.infer<typeof callIntakeSnapshotSchema>;

/** Request body for `POST /api/appointments/:id/call-capture`. */
export const recordCallCaptureRequestSchema = z.object({
  snapshot: callIntakeSnapshotSchema,
});

export type RecordCallCaptureRequest = z.infer<typeof recordCallCaptureRequestSchema>;

/** Response for `POST /api/appointments/:id/call-capture`. */
export const recordCallCaptureResponseSchema = z.object({
  appointmentId: z.string().uuid(),
  capturedAt: z.string(),
});

export type RecordCallCaptureResponse = z.infer<typeof recordCallCaptureResponseSchema>;

/**
 * Get upcoming appointments request (query params).
 */
export const getUpcomingAppointmentsRequestSchema = z.object({
  patientId: z.string().uuid(),
  status: z.string().optional(), // Comma-separated statuses
  upcoming: z.boolean().optional(),
  limit: z.number().int().min(1).max(50).optional(),
});

export type GetUpcomingAppointmentsRequest = z.infer<typeof getUpcomingAppointmentsRequestSchema>;

/**
 * Get appointments response.
 */
export const getAppointmentsResponseSchema = z.object({
  appointments: z.array(appointmentWithRelationsSchema),
});

export type GetAppointmentsResponse = z.infer<typeof getAppointmentsResponseSchema>;

// =============================================================================
// Schedule Types (for Calendar/Table View)
// =============================================================================

/**
 * Schedule query parameters.
 */
export const scheduleQuerySchema = z.object({
  startDate: z.string(), // YYYY-MM-DD
  endDate: z.string(), // YYYY-MM-DD
  providerIds: z.array(z.string().uuid()).optional(),
  locationIds: z.array(z.string().uuid()).optional(),
  statuses: z.array(z.number()).optional(),
  search: z.string().optional(),
});

export type ScheduleQuery = z.infer<typeof scheduleQuerySchema>;

/**
 * Query parameters for the scheduled-appointments XLSX export. Inclusive
 * date range over appointment start time.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const exportScheduleQuerySchema = z.object({
  from: z.string().regex(ISO_DATE, 'Must be YYYY-MM-DD format'),
  to: z.string().regex(ISO_DATE, 'Must be YYYY-MM-DD format'),
});

export type ExportScheduleQuery = z.infer<typeof exportScheduleQuerySchema>;

/**
 * Schedule appointment (flattened for easy display).
 */
export const scheduleAppointmentSchema = z.object({
  id: z.string().uuid(),
  startTime: z.string(),
  endTime: z.string(),
  status: z.number(),
  reasonForVisit: z.string().nullable(),
  notes: z.string().nullable(),
  isTelehealth: z.boolean(),
  confirmationNumber: z.string().nullable(),
  patient: z
    .object({
      id: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullable(),
  provider: z
    .object({
      id: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullable(),
  location: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      timezone: z.string(),
    })
    .nullable(),
  appointmentType: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      color: z.string().nullable(),
    })
    .nullable(),
});

export type ScheduleAppointment = z.infer<typeof scheduleAppointmentSchema>;

/**
 * Day with appointments.
 */
export const scheduleDaySchema = z.object({
  date: z.string(), // YYYY-MM-DD
  appointments: z.array(scheduleAppointmentSchema),
});

export type ScheduleDay = z.infer<typeof scheduleDaySchema>;

/**
 * Provider summary for legend.
 */
export const scheduleProviderSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
});

export type ScheduleProvider = z.infer<typeof scheduleProviderSchema>;

/**
 * Schedule response.
 */
export const scheduleResponseSchema = z.object({
  days: z.array(scheduleDaySchema),
  providers: z.array(scheduleProviderSchema),
  totalCount: z.number(),
});

export type ScheduleResponse = z.infer<typeof scheduleResponseSchema>;

// =============================================================================
// Day Slots Types (for Slots Timeline View)
// =============================================================================

/**
 * Query parameters for day slots.
 */
export const daySlotsQuerySchema = z.object({
  date: z.string(), // YYYY-MM-DD
  providerIds: z.array(z.string().uuid()).optional(),
  locationIds: z.array(z.string().uuid()).optional(),
});

export type DaySlotsQuery = z.infer<typeof daySlotsQuerySchema>;

/**
 * Slot status for timeline display.
 */
export const slotDisplayStatus = z.enum(['open', 'booked', 'unavailable']);

export type SlotDisplayStatus = z.infer<typeof slotDisplayStatus>;

/**
 * Individual slot in the timeline.
 */
export const daySlotSchema = z.object({
  id: z.string(),
  startTime: z.string(), // ISO datetime
  endTime: z.string(), // ISO datetime
  durationMinutes: z.number(),
  status: slotDisplayStatus,
  // Appointment type (open slots carry it; null when unknown). The id flows into
  // booking so the EHR write has the type it needs to sync.
  appointmentTypeId: z.string().nullable(),
  appointmentTypeName: z.string().nullable(),
  // Every type an open slot is known to take (its EHR slots' types, ADR-273), or
  // null when that isn't known and any type may book it. A move of an appointment
  // whose type is missing here is refused (ADR-313), so the grid marks the slot.
  acceptedTypeIds: z.array(z.string()).nullable(),
  // For booked slots
  appointment: z
    .object({
      id: z.string().uuid(),
      status: z.number(),
      isTelehealth: z.boolean(),
      reasonForVisit: z.string().nullable(),
      notes: z.string().nullable(),
      // The linked patient — null when the appointment has no patient record yet.
      // Powers the hover card and the in-grid patient search (highlight/dim).
      // PHI (name/phone/dob): authenticated, tenant-scoped response only — never
      // logged. `dob` is an ISO yyyy-mm-dd string; `phone` is normalized E.164.
      patient: z
        .object({
          initials: z.string(),
          name: z.string().nullable(),
          phone: z.string().nullable(),
          dob: z.string().nullable(),
        })
        .nullable(),
    })
    .nullable(),
});

export type DaySlot = z.infer<typeof daySlotSchema>;

/**
 * One day-view column: a provider AT a location, with their slots for the day.
 * A provider who works multiple locations appears as multiple columns. `location`
 * is null only for locationless rows (e.g. telehealth).
 */
export const providerDaySlotsSchema = z.object({
  provider: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
  }),
  location: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      timezone: z.string(),
    })
    .nullable(),
  slots: z.array(daySlotSchema),
});

export type ProviderDaySlots = z.infer<typeof providerDaySlotsSchema>;

/**
 * Summary statistics for the day.
 */
export const daySlotsSummarySchema = z.object({
  totalOpen: z.number(),
  totalBooked: z.number(),
  providerCount: z.number(),
});

export type DaySlotsSummary = z.infer<typeof daySlotsSummarySchema>;

/**
 * Response for day slots endpoint.
 */
export const daySlotsResponseSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  providers: z.array(providerDaySlotsSchema),
  summary: daySlotsSummarySchema,
});

export type DaySlotsResponse = z.infer<typeof daySlotsResponseSchema>;

// =============================================================================
// Computed Slot Types (for Slot Computation Engine)
// =============================================================================

/**
 * Request to compute available slots using the progressive day scan algorithm.
 */
export const computeSlotsRequestSchema = z.object({
  /** Single provider UUID, array, or omit for all providers */
  providerId: z.union([z.string().uuid(), z.array(z.string().uuid()).min(1)]).optional(),
  /** Location UUID or omit for all locations */
  locationId: z.string().uuid().optional(),
  appointmentTypeId: z.string().uuid(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD format'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD format'),
  maxSlots: z.number().int().min(1).max(200).optional().default(20),
  /** Max slots to return per day — spreads results across days */
  maxSlotsPerDay: z.number().int().min(1).max(50).optional(),
  /** Whether the patient is new (for maxNewPerDay constraint) */
  isNewPatient: z.boolean().optional().default(false),
  /** Optional time block filter — only return slots within these time ranges */
  timeBlocks: z.array(z.enum(['morning', 'afternoon', 'evening'])).optional(),
});

export type ComputeSlotsRequest = z.infer<typeof computeSlotsRequestSchema>;

/**
 * A single computed available slot. Mirrors `timeSlotSchema`'s provider
 * name fields — see that schema's doc for the meaning of each variant.
 */
export const computedSlotSchema = z.object({
  startTime: z.string(), // ISO datetime
  endTime: z.string(), // ISO datetime
  durationMinutes: z.number(),
  providerId: z.string().uuid(),
  providerName: z.string(),
  providerDisplayName: z.string(),
  providerSpokenName: z.string(),
  providerCredential: z.string().nullable(),
  locationId: z.string().uuid(),
  locationName: z.string(),
  locationTimezone: z.string(),
  appointmentTypeId: z.string().uuid(),
  appointmentTypeName: z.string(),
  displayTime: z.string(), // Human-readable for speech
});

export type ComputedSlot = z.infer<typeof computedSlotSchema>;

/**
 * Response from the slot computation endpoint.
 */
export const computeSlotsResponseSchema = z.object({
  slots: z.array(computedSlotSchema),
  /** Number of days scanned before finding enough slots */
  daysScanned: z.number(),
  /** Total computation time in milliseconds */
  computationMs: z.number(),
});

export type ComputeSlotsResponse = z.infer<typeof computeSlotsResponseSchema>;

// =============================================================================
// Offerable Slots (unified engine)
// =============================================================================

/**
 * Request for the unified offerable-slots engine
 * (`POST /api/appointments/offerable-slots`). This is the SINGLE source of
 * truth every scheduling surface (voice agent, browser test, drawer preview,
 * slot search, referrals) calls, so what is offered, previewed, and booked can
 * never diverge.
 *
 * Pipeline: scraped pool → type gate (slot type = null OR requested) → the
 * appointment type's constraints (preferred-day/time windows, booking limits,
 * lead time, conflicts) → shaped offers.
 */
export const offerableSlotsRequestSchema = z
  .object({
    /**
     * Single provider UUID or array (multi-provider search). May be omitted only
     * with `openScope` — then every provider with open slots for the type (in
     * `locationId`, when given) is searched.
     */
    providerId: z.union([z.string().uuid(), z.array(z.string().uuid()).min(1)]).optional(),
    /**
     * Single location UUID or array. Constraints + the scraped pool are scoped to
     * one location per pass; several locations (or none, with `openScope`) run one
     * pass per location and merge.
     */
    locationId: z.union([z.string().uuid(), z.array(z.string().uuid()).min(1)]).optional(),
    /**
     * Opt in to leaving `providerId` / `locationId` open ("any"). Without it both
     * are required, so a caller that loses its scope fails closed with a 422
     * instead of silently searching the whole org (ADR-267). Only operator
     * surfaces (Find Slots, its chat) set it; agents never do.
     */
    openScope: z.boolean().optional(),
    /** Required: constraints and the type gate are per appointment type. */
    appointmentTypeId: z.string().uuid(),
    /**
     * Explicit inclusive search window (YYYY-MM-DD). When BOTH are set, the
     * engine queries that exact range and skips progressive chunking — used when
     * the patient asked for a specific day/week.
     */
    fromDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    toDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    /**
     * Horizon cap for the open-ended ("earliest available") search. The engine
     * scans in 2-week chunks and short-circuits once `maxSlots` is found, never
     * scanning past this many days ahead. Ignored when fromDate/toDate are set.
     */
    daysAhead: z.number().int().min(1).max(90).optional(),
    /**
     * Page size. Raised from 200 to 250, then to 500 alongside the available
     * route's `count` so the two ceilings stay one number, for the reason the voice agent found the
     * hard way: a relative ask ("later in the week") is answered by ranking the
     * DAYS the search came back with, and the engine returns slots earliest-first,
     * so a page that stops mid-window silently truncates the day set rather than
     * the slot list. On a five-day window at eight offerable slots a day the old
     * 25-slot page reached Wednesday and the agent offered Wednesday for an ask
     * that should have landed on Thursday. See ADR-214 / `WIDE_SEARCH_COUNT`.
     */
    maxSlots: z.number().int().min(1).max(500).optional().default(5),
    /** Cap slots per day to spread results across days. */
    maxSlotsPerDay: z.number().int().min(1).max(50).optional(),
    isNewPatient: z.boolean().optional().default(false),
    /** Headline selection — 'latest' returns the latest matching slot. */
    sortBy: z.enum(['earliest', 'latest']).optional().default('earliest'),
    /**
     * One time-of-day block or several. A slot matches if it falls in ANY of the
     * requested blocks (e.g. `['morning', 'evening']` offers morning and evening
     * slots but not afternoon). Single values stay supported for existing callers.
     */
    preferredTimeOfDay: z
      .union([
        z.enum(['morning', 'afternoon', 'evening']),
        z.array(z.enum(['morning', 'afternoon', 'evening'])).min(1),
      ])
      .optional(),
    preferredDaysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
    /** `HH:MM` clinic-local lower bound on slot start. */
    earliestTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
    /** `HH:MM` clinic-local upper bound on slot start. */
    latestTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
  })
  .superRefine((request, ctx) => {
    if (request.openScope) return;
    for (const field of ['providerId', 'locationId'] as const) {
      if (request[field] === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `${field} is required unless openScope is set`,
        });
      }
    }
  });

export type OfferableSlotsRequest = z.infer<typeof offerableSlotsRequestSchema>;
/** Pre-parse shape — fields with schema defaults (maxSlots, isNewPatient,
 *  sortBy) are optional. The engine applies the same defaults defensively, so
 *  callers and the service can use this looser type. */
export type OfferableSlotsRequestInput = z.input<typeof offerableSlotsRequestSchema>;

/**
 * A single offerable slot. Superset of the agent's time-slot and the drawer's
 * computed-slot shapes so one response serves every consumer.
 */
export const offerableSlotSchema = z.object({
  slotId: z.string(),
  startTime: z.string(), // ISO datetime
  endTime: z.string(), // ISO datetime
  durationMinutes: z.number(),
  providerId: z.string().uuid(),
  providerName: z.string(),
  providerDisplayName: z.string(),
  providerSpokenName: z.string(),
  providerCredential: z.string().nullable(),
  locationId: z.string().uuid(),
  locationName: z.string(),
  locationTimezone: z.string(),
  appointmentTypeId: z.string().uuid(),
  appointmentTypeName: z.string(),
  displayTime: z.string(), // Human-readable for speech
});

export type OfferableSlot = z.infer<typeof offerableSlotSchema>;

export const offerableSlotsResponseSchema = z.object({
  slots: z.array(offerableSlotSchema),
  /** Number of 2-week chunks scanned before short-circuiting (1 = common case). */
  chunksScanned: z.number(),
  /** Total engine time in milliseconds. */
  computationMs: z.number(),
});

export type OfferableSlotsResponse = z.infer<typeof offerableSlotsResponseSchema>;

// =============================================================================
// Waitlist (ADR-271)
// =============================================================================

/** A clinic-local wall-clock time, "HH:MM" on the 24-hour clock. */
const waitlistClockTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be HH:MM (24-hour)');

/**
 * When a waitlisted patient can come. Every field is optional; absent means
 * "any". Days are 0=Sunday..6=Saturday. The time range is the window an
 * appointment should START in: [earliestTime, latestTime).
 */
export const waitlistPreferencesSchema = z
  .object({
    preferredDaysOfWeek: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    earliestTime: waitlistClockTimeSchema.optional(),
    latestTime: waitlistClockTimeSchema.optional(),
  })
  .refine((p) => !p.earliestTime || !p.latestTime || p.earliestTime < p.latestTime, {
    message: 'earliestTime must be before latestTime',
    path: ['latestTime'],
  });
export type WaitlistPreferencesInput = z.infer<typeof waitlistPreferencesSchema>;

export const WAITLIST_PRIORITIES = ['high', 'normal', 'low'] as const;
export const waitlistPrioritySchema = z.enum(WAITLIST_PRIORITIES);
export type WaitlistPriority = z.infer<typeof waitlistPrioritySchema>;

/**
 * Request shape for `POST /api/waitlist`: the voice agent (when a patient opts
 * in on a call) or staff. EHR-sourced entries arrive through the EHR sync.
 *
 * Absent `providerId` / `locationId` means any provider / any location. With
 * `appointmentId` (the booked appointment the patient wants sooner), the
 * appointment type is taken from that appointment and `appointmentTypeId` is
 * ignored.
 */
export const addToWaitlistRequestSchema = z.object({
  patientId: z.string().uuid(),
  appointmentId: z.string().uuid().optional(),
  appointmentTypeId: z.string().uuid().optional(),
  providerId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  preferences: waitlistPreferencesSchema.optional(),
  priority: waitlistPrioritySchema.optional(),
  notes: z.string().max(1000).optional(),
  /**
   * @deprecated Free text from voice workers deployed before days + time range
   * (ADR-271). Kept for one release so a mid-rollout call isn't lost; the API
   * folds it into `notes`.
   */
  preferredTimeframe: z.string().max(200).optional(),
});
export type AddToWaitlistRequest = z.infer<typeof addToWaitlistRequestSchema>;

/** Response: the waitlist entry's generated id. */
export const addToWaitlistResponseSchema = z.object({
  id: z.string().uuid(),
});
export type AddToWaitlistResponse = z.infer<typeof addToWaitlistResponseSchema>;

// =============================================================================
// Scheduling Chat
// =============================================================================

/**
 * POST /api/appointments/scheduling-chat. No `organizationId` in `context`:
 * the server fills it from the caller's session, never from the body.
 */
export const schedulingChatRequestSchema = z.object({
  message: z.string().min(1),
  context: z.object({
    startDate: z.string(),
    endDate: z.string(),
    patientType: z.enum(['new', 'existing']),
    timeBlocks: z.array(z.string()),
    providerIds: z.array(z.string()),
    locationIds: z.array(z.string()),
    appointmentTypeId: z.string().optional(),
  }),
  conversationHistory: z.array(
    z.object({
      role: z.enum(['user', 'assistant']),
      content: z.string(),
    })
  ),
});

export type SchedulingChatRequest = z.infer<typeof schedulingChatRequestSchema>;
